import * as React from "react";

import { FormattedMessage } from "react-intl";
import { useSelector } from "react-redux";

import { Text } from "@ps-design-system/components/Text";
import LightbulbIcon from "@ps-design-system/icons/Lightbulb/Lightbulb32";

import { selectAuthAccessToken } from "../../../../components/src/app-main/selectors/auth.selectors";
import { selectProfileSSOid } from "../../../../components/src/app-main/selectors/profile.selectors";

import { ActivateListingsPageContent } from "./ActivateListingsPageContent";
import { ActivateListingsPageSkeleton } from "./ActivateListingsPageSkeleton";
import { useActivateListingsData } from "./useActivateListingsData";

type ActivateListingsPageProps = {
  params: { spaceId: string };
};

const ActivateListingsPageShell = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  return (
    <div
      className="ps-container-lg-margin ps-container-fixed tw-py-64"
      data-testid="activate-listings-page"
    >
      <div className="ps-row">
        <div className="col-xs-12">{children}</div>
      </div>
    </div>
  );
};

const ActivateListingsPage = ({
  params: { spaceId },
}: ActivateListingsPageProps) => {
  const accessToken = useSelector(selectAuthAccessToken);
  const ssoId = useSelector(selectProfileSSOid);
  const {
    inactiveUseTypes,
    isFetching,
    space,
    amenitiesByUseType,
    isInstantBookEligible,
  } = useActivateListingsData({ spaceId });

  if (!accessToken) {
    return null;
  }

  if (isFetching || !space || !ssoId) {
    return <ActivateListingsPageSkeleton />;
  }

  // Ensure only space owners can access this page
  if (space.ownerId !== ssoId) {
    // Ensure only space owners can access this page
    window.location.href = "/mylistings";
    return null;
  }

  return (
    <ActivateListingsPageShell>
      <Text
        typeface="headline-5-b"
        element="h1"
        testId="activate-listings-page-header"
        className="tw-m-0"
      >
        <FormattedMessage
          id="hosting.activate_listings.page_header"
          defaultMessage="Which activities would you like to host?"
        />
      </Text>

      <div className="tw-max-w-[680px]">
        <Text typeface="body-2" element="p">
          <FormattedMessage
            id="hosting.activate_listings.page_description"
            defaultMessage="Select the types of activities you would like to host. For each selection, we'll create a customized listing unique to that activity."
          />
        </Text>
        <span className="tw-flex tw-items-start tw-gap-8 tw-mt-16">
          <LightbulbIcon className="tw-flex-shrink-0 tw-text-yellow-600" />
          <Text typeface="body-2" element="p">
            <FormattedMessage
              id="hosting.activate_listings.page_tip"
              defaultMessage="Tip: To be approved for Meetings your photos must display a meeting or workshop setup including tables and chairs. We also recommend adding photos of your AV setup."
            />
          </Text>
        </span>
      </div>

      <ActivateListingsPageContent
        space={space}
        inactiveUseTypes={inactiveUseTypes}
        amenitiesByUseType={amenitiesByUseType}
        isInstantBookEligible={isInstantBookEligible}
      />
    </ActivateListingsPageShell>
  );
};

export { ActivateListingsPage };
